import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ShoppingCart as OrderIcon,
  FileDownload as ExportIcon,
  Warning as WarningIcon,
  CheckCircle as ReceivedIcon
} from '@mui/icons-material';
import { useJob } from '../contexts/JobContext';

/**
 * Equipment Shortage Report component
 * Shows equipment shortages across jobs with order status
 */
export default function EquipmentShortageReport() {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shortageData, setShortageData] = useState(null);
  const [selectedJob, setSelectedJob] = useState('All');
  
  // Order dialog state
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [orderData, setOrderData] = useState({
    orderReference: '',
    orderedQuantity: 0,
    orderDate: new Date().toISOString().split('T')[0],
    expectedArrival: '',
    supplier: '',
    notes: ''
  });
  
  // Get job context for filtering
  const { jobList } = useJob();
  
  // Load shortages on mount and when selected job changes
  useEffect(() => {
    loadShortageReport();
  }, [selectedJob]);
  
  // Load shortage report data
  const loadShortageReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedJob && selectedJob !== 'All') {
        params.append('jobId', selectedJob);
      }
      
      // In a real implementation, this would fetch from the API
      const response = await fetch(`/api/planning/shortages?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load shortage report');
      }
      
      const data = await response.json();
      setShortageData(data);
    } catch (err) {
      console.error('Error loading shortage report:', err);
      setError(err.message || 'Failed to load shortage report');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle job filter change
  const handleJobChange = (event) => {
    setSelectedJob(event.target.value);
  };
  
  // Handle opening order dialog
  const handleOpenOrderDialog = (item) => {
    setCurrentItem(item);
    setOrderData({
      orderReference: `PO-${new Date().getTime().toString().slice(-6)}`,
      orderedQuantity: item.shortageAmount,
      orderDate: new Date().toISOString().split('T')[0],
      expectedArrival: '',
      supplier: '',
      notes: `For job: ${item.job?.name || 'Unknown'}`
    });
    setShowOrderDialog(true);
  };
  
  // Handle submitting order
  const handleSubmitOrder = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, this would call the API to create an order
      // and update the status of the job equipment plan
      const response = await fetch(`/api/planning/jobs/${currentItem.job.id}/order-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planIds: [currentItem.id],
          orderStatus: 'ORDERED',
          orderedQuantity: orderData.orderedQuantity,
          orderDate: orderData.orderDate,
          expectedArrival: orderData.expectedArrival,
          orderReference: orderData.orderReference
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update order status');
      }
      
      // Create order record
      const orderResponse = await fetch('/api/planning/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderReference: orderData.orderReference,
          orderDate: orderData.orderDate,
          expectedArrival: orderData.expectedArrival,
          supplier: orderData.supplier,
          notes: orderData.notes,
          items: [
            {
              equipmentId: currentItem.equipment.id,
              jobId: currentItem.job.id,
              orderedQuantity: orderData.orderedQuantity,
              notes: `For job: ${currentItem.job.name}`
            }
          ]
        })
      });
      
      if (!orderResponse.ok) {
        throw new Error('Failed to create order');
      }
      
      // Close dialog and reload data
      setShowOrderDialog(false);
      loadShortageReport();
    } catch (err) {
      console.error('Error submitting order:', err);
      setError(err.message || 'Failed to submit order');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle export
  const handleExport = () => {
    // Convert shortage data to CSV
    if (!shortageData) return;
    
    let csvContent = 'Job,Site,Equipment,Manufacturer,Model,Needed,Available,Shortage,Order Status,Order Ref\n';
    
    // Add rows
    Object.values(shortageData.jobGroups).forEach(group => {
      group.shortages.forEach(shortage => {
        csvContent += [
          `"${group.job.name}"`,
          `"${shortage.site?.name || 'Unassigned'}"`,
          `"${shortage.equipment.description}"`,
          `"${shortage.equipment.manufacturer || ''}"`,
          `"${shortage.equipment.model || ''}"`,
          shortage.neededQuantity,
          shortage.availableQuantity,
          shortage.shortageAmount,
          shortage.orderStatus,
          `"${shortage.orderReference || ''}"`
        ].join(',') + '\n';
      });
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `equipment-shortages-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Format order status display
  const renderOrderStatus = (status, shortage) => {
    switch (status) {
      case 'not_ordered':
        return (
          <Chip 
            label="Not Ordered" 
            color="error"
            size="small"
            icon={<WarningIcon />}
          />
        );
      case 'partially_ordered':
        return (
          <Chip 
            label="Partially Ordered" 
            color="warning"
            size="small"
          />
        );
      case 'ordered':
        return (
          <Chip 
            label="Ordered" 
            color="info"
            size="small"
            icon={<OrderIcon />}
          />
        );
      case 'partially_received':
        return (
          <Chip 
            label="Partially Received" 
            color="success"
            variant="outlined"
            size="small"
          />
        );
      case 'received':
        return (
          <Chip 
            label="Received" 
            color="success"
            size="small"
            icon={<ReceivedIcon />}
          />
        );
      default:
        return (
          <Chip 
            label="Unknown" 
            size="small"
          />
        );
    }
  };
  
  return (
    <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Equipment Shortage Report
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !shortageData || shortageData.totalShortages === 0}
            sx={{ mr: 1 }}
          >
            Export CSV
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadShortageReport}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="job-filter-label">Filter by Job</InputLabel>
              <Select
                labelId="job-filter-label"
                value={selectedJob}
                onChange={handleJobChange}
                label="Filter by Job"
              >
                <MenuItem value="All">All Jobs</MenuItem>
                {jobList.map((job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {shortageData && (
            <Grid item xs={12} sm={6} md={8}>
              <Typography variant="body2" color="text.secondary">
                Showing {shortageData.totalShortages} equipment shortages 
                {selectedJob !== 'All' ? ' for selected job' : ' across all jobs'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!shortageData || shortageData.totalShortages === 0 ? (
            <Alert severity="info">
              No equipment shortages found. All equipment needs are satisfied.
            </Alert>
          ) : (
            <Box>
              {Object.values(shortageData.jobGroups).map((jobGroup) => (
                <Box key={jobGroup.job.id} mb={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {jobGroup.job.name}
                    </Typography>
                    <Chip 
                      label={`${jobGroup.shortages.length} items needed`}
                      color="error"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Equipment</TableCell>
                          <TableCell>Site</TableCell>
                          <TableCell>Manufacturer/Model</TableCell>
                          <TableCell align="center">Needed</TableCell>
                          <TableCell align="center">Available</TableCell>
                          <TableCell align="center">Shortage</TableCell>
                          <TableCell>Order Status</TableCell>
                          <TableCell>Order Ref#</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobGroup.shortages.map((shortage) => (
                          <TableRow key={shortage.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {shortage.equipment.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {shortage.site?.name || 'Unassigned'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {shortage.equipment.manufacturer} {shortage.equipment.model}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">{shortage.neededQuantity}</TableCell>
                            <TableCell align="center">{shortage.availableQuantity}</TableCell>
                            <TableCell align="center">
                              <Typography color="error" fontWeight="bold">
                                -{shortage.shortageAmount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {renderOrderStatus(shortage.orderStatus)}
                            </TableCell>
                            <TableCell>
                              {shortage.orderReference || '-'}
                            </TableCell>
                            <TableCell>
                              {['NOT_ORDERED', 'PARTIALLY_ORDERED'].includes(shortage.orderStatus) && (
                                <Tooltip title="Create Order">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleOpenOrderDialog(shortage)}
                                  >
                                    <OrderIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
      
      {/* Order Dialog */}
      <Dialog
        open={showOrderDialog}
        onClose={() => setShowOrderDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Order</DialogTitle>
        <DialogContent>
          {currentItem && (
            <Box mt={1}>
              <Typography variant="subtitle2" gutterBottom>
                Create order for: {currentItem.equipment.description}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {currentItem.equipment.manufacturer} {currentItem.equipment.model}
              </Typography>
              <Typography variant="body2" gutterBottom>
                For job: {currentItem.job.name}
              </Typography>
              
              <Box sx={{ my: 2, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Typography color="error.main" fontWeight="medium">
                  Shortage: {currentItem.shortageAmount} units
                </Typography>
              </Box>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Order Reference"
                    fullWidth
                    value={orderData.orderReference}
                    onChange={(e) => setOrderData({ ...orderData, orderReference: e.target.value })}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Supplier"
                    fullWidth
                    value={orderData.supplier}
                    onChange={(e) => setOrderData({ ...orderData, supplier: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Order Date"
                    type="date"
                    fullWidth
                    value={orderData.orderDate}
                    onChange={(e) => setOrderData({ ...orderData, orderDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Expected Arrival"
                    type="date"
                    fullWidth
                    value={orderData.expectedArrival}
                    onChange={(e) => setOrderData({ ...orderData, expectedArrival: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Quantity to Order"
                    type="number"
                    fullWidth
                    value={orderData.orderedQuantity}
                    onChange={(e) => setOrderData({ 
                      ...orderData, 
                      orderedQuantity: Math.max(1, parseInt(e.target.value) || 1) 
                    })}
                    inputProps={{ min: 1 }}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={2}
                    value={orderData.notes}
                    onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOrderDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSubmitOrder}
            startIcon={<OrderIcon />}
            disabled={
              !orderData.orderReference || 
              !orderData.orderDate || 
              orderData.orderedQuantity < 1
            }
          >
            Submit Order
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}